import {
  ASSESSMENT_FIELD_NAME_MAX_LENGTH,
  CANDIDATE_ALIGN_FIELD_NAME,
  CefrAssessmentFieldNameToLanguage,
} from "@repo/app-types/entities/assessment";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInsertClient,
} from "@repo/app-types/entities/customField";
import {
  checkIsCustomFieldDefinitionImportanceMustHave,
  CustomFieldDefinitionImportanceSetting,
  getCustomFieldDisplayName,
} from "@repo/app-types/entities/customField";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconLanguage, IconTrash, IconUser } from "@tabler/icons-react";
import type React from "react";
import { ToggleButton } from "react-aria-components";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import {
  renderCustomFieldDefinitionImportanceMustHaveIcon,
  renderCustomFieldDefinitionImportanceOther,
} from "./helpers.js";

interface AssessmentEditorRowProps {
  field: CustomFieldDefinition | CustomFieldDefinitionInsertClient;
  onRenameField: (newName: string) => void;
  onBlur: (value: string) => void;
  onRemoveField: () => void;
  onChangeImportance: (
    value: CustomFieldDefinitionImportanceSetting | undefined,
  ) => void;
  fieldError: string;
  placeholder: string;
}

export const AssessmentEditorRow: React.FC<AssessmentEditorRowProps> = ({
  field,
  onRenameField,
  onBlur,
  onRemoveField,
  onChangeImportance,
  fieldError,
  placeholder,
}) => {
  const handleMustHaveClick = () => {
    // If current importance is "must have", set to undefined
    if (
      field.settings?.importance ===
      CustomFieldDefinitionImportanceSetting.MustHave
    ) {
      onChangeImportance(undefined);
    } else {
      // If current importance is undefined (not "must have"), set to "must have"
      onChangeImportance(CustomFieldDefinitionImportanceSetting.MustHave);
    }
  };

  const renderMustHaveIcon = () => {
    const isMustHave = checkIsCustomFieldDefinitionImportanceMustHave(
      field.settings?.importance,
    );

    const icon = isMustHave
      ? renderCustomFieldDefinitionImportanceMustHaveIcon()
      : renderCustomFieldDefinitionImportanceOther();

    const tooltipContent = isMustHave
      ? "Qualification is a must have, click to remove the must have requirement."
      : "Qualification is not a must have, click to set the must have requirement.";

    return (
      <TooltipTrigger>
        <ToggleButton
          isSelected={isMustHave}
          onChange={handleMustHaveClick}
          className="-ml-1 shrink-0 rounded-sm p-1 transition-colors hover:bg-gray-100"
          aria-label="Must have toggle"
        >
          {icon}
        </ToggleButton>
        <SimpleTooltip>{tooltipContent}</SimpleTooltip>
      </TooltipTrigger>
    );
  };

  const renderFieldContent = () => {
    if (field.field_name in CefrAssessmentFieldNameToLanguage && !fieldError) {
      return (
        <div className="flex w-full flex-row items-center space-x-2">
          <IconLanguage className="h-4 w-4" />
          <span>{getCustomFieldDisplayName(field)}</span>
        </div>
      );
    }

    if (field.display_name === CANDIDATE_ALIGN_FIELD_NAME && !fieldError) {
      return (
        <div className="flex w-full flex-row items-center space-x-2">
          <IconUser className="h-4 w-4" />
          <span>{getCustomFieldDisplayName(field)}</span>
        </div>
      );
    }

    return (
      <div className="flex w-full flex-row items-center space-x-2">
        {renderMustHaveIcon()}
        <div className="w-full">
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Textarea
                value={getCustomFieldDisplayName(field)}
                onChange={onRenameField}
                onBlur={(e) => onBlur(e.target.value)}
                placeholder={placeholder}
                errorDescription={fieldError}
                maxLength={ASSESSMENT_FIELD_NAME_MAX_LENGTH}
                variant={TextareaVariant.MINIMAL}
                shouldDisableNewline
                isDisabled={!allowed}
              />
            )}
          </Can>
        </div>
      </div>
    );
  };

  return (
    <tr className="group relative border-b border-gray-900/5">
      <td className="px-3 py-2 text-sm text-foreground">
        {renderFieldContent()}
      </td>
      <td className="absolute top-1/2 right-2 -translate-y-1/2">
        <Can I={PermissionActions.UPDATE} a={PermissionSubjects.JOB_OPENINGS}>
          <div className="invisible group-hover:visible">
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconTrash className="h-4 w-4 text-red-500" />}
              onClick={onRemoveField}
            />
          </div>
        </Can>
      </td>
    </tr>
  );
};
